import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

const DECIMAL_MESSAGE = `Up to {precision} digits, {scale} decimal places.`;

export const DECIMAL_ERROR = 'decimal';

export type DecimalValidationError = {
  message: string;
};

export function decimalValidator(precision: number, scale: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();

    if (!value) {
      return null;
    }

    return validateDecimal(value, precision, scale);
  };
}

function validateDecimal(value: string, precision: number, scale: number): ValidationErrors | null {
  const sanitizedValue = removeLeadingSign(value);

  if (sanitizedValue.includes('e') || sanitizedValue.includes('E')) {
    return createDecimalError(DECIMAL_MESSAGE, precision, scale);
  }

  const parts = sanitizedValue.split('.');

  if (parts.length > 2) {
    return createDecimalError(DECIMAL_MESSAGE, precision, scale);
  }

  const [integerPart = '', fractionalPart = ''] = parts;

  if (!integerPart && !fractionalPart) {
    return createDecimalError(DECIMAL_MESSAGE, precision, scale);
  }

  if (!containsOnlyDigits(integerPart) || !containsOnlyDigits(fractionalPart)) {
    return createDecimalError(DECIMAL_MESSAGE, precision, scale);
  }

  if (fractionalPart.length > scale) {
    return createDecimalError(DECIMAL_MESSAGE, precision, scale);
  }

  if (integerPart.length > precision - scale) {
    return createDecimalError(DECIMAL_MESSAGE, precision, scale);
  }

  return null;
}

function removeLeadingSign(value: string): string {
  if (value.startsWith('+') || value.startsWith('-')) {
    return value.slice(1);
  }

  return value;
}

function containsOnlyDigits(value: string): boolean {
  for (const character of value) {
    if (character < '0' || character > '9') {
      return false;
    }
  }

  return true;
}

function createDecimalError(message: string, precision: number, scale: number): ValidationErrors {
  const resolvedMessage = message.replace('{precision}', String(precision)).replace('{scale}', String(scale));

  return {
    [DECIMAL_ERROR]: {
      message: resolvedMessage,
    } satisfies DecimalValidationError,
  };
}
